class FormValidator {
  constructor(form) {
    this.form = form;
    this.form.setAttribute("novalidate", "");
    this.errorMessages = new Map();
    this.setupValidation();
  }

  setupValidation() {
    // Add blur event listeners to all form inputs
    this.form.querySelectorAll("input, textarea, select").forEach((input) => {
      input.addEventListener("blur", () => this.validateField(input));
      input.addEventListener("input", () => {
        if (this.errorMessages.has(input)) {
          this.validateField(input);
        }
      });
    });

    // Add submit event listener to the form account
    if (!location.pathname.includes("/cart") && !location.pathname.includes("/account")) {
      const btn = this.form.querySelector('[type="submit"]');
      this.form.addEventListener("submit", (e) => {
        e.preventDefault();

        if (btn.getAttribute("name") === "checkout") {
          this.form.submit();
          return;
        }
        let isValid = true;
        btn.setAttribute("aria-busy", "true");

        // Validate all fields
        this.form
          .querySelectorAll("input, textarea, select")
          .forEach((input) => {
            if (!this.validateField(input)) {
              isValid = false;
            }
          });

        if (isValid) {
          this.form.submit();
        } else {
          btn.setAttribute("aria-busy", "false");
        }
      });
    } else {
      this.form.addEventListener("submit", (e) => {
        const btn = document.querySelector('.cart-form button[type="submit"]');
        btn?.setAttribute("aria-busy", "true");
      });
    }
  }

  validateField(input) {
    // Remove existing error message
    this.removeError(input);

    // Skip validation for hidden fields
    if (input.type === "hidden") {
      return true;
    }

    let isValid = true;
    let errorMessage = "";

    // Required field validation
    if (input.hasAttribute("required") && !input.value.trim()) {
      isValid = false;
      errorMessage = this.getErrorMessage("form_error_required", {
        field: this.getFieldName(input),
      });
    }
    // Email validation
    else if (input.type === "email" && input.value.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(input.value.trim())) {
        isValid = false;
        errorMessage = this.getErrorMessage("form_error_email");
      }
    }
    // Pattern validation
    else if (input.hasAttribute("pattern") && input.value.trim()) {
      const pattern = new RegExp(input.getAttribute("pattern"));
      if (!pattern.test(input.value.trim())) {
        isValid = false;
        errorMessage =
          input.getAttribute("title") ||
          this.getErrorMessage("form_error_pattern");
      }
    }
    // Min length validation
    else if (input.hasAttribute("minlength") && input.value.trim()) {
      const minLength = parseInt(input.getAttribute("minlength"));
      if (input.value.trim().length < minLength) {
        isValid = false;
        errorMessage = this.getErrorMessage("form_error_min_length", {
          min: minLength,
        });
      }
    }
    // Max length validation
    else if (input.hasAttribute("maxlength") && input.value.trim()) {
      const maxLength = parseInt(input.getAttribute("maxlength"));
      if (input.value.trim().length > maxLength) {
        isValid = false;
        errorMessage = this.getErrorMessage("form_error_max_length", {
          max: maxLength,
        });
      }
    }

    if (!isValid) {
      this.showError(input, errorMessage);
    }

    return isValid;
  }

  getErrorMessage(key, params = {}) {
    let message = window.themeSettings[key] || this.getDefaultErrorMessage(key);

    // Replace placeholders with actual values
    Object.entries(params).forEach(([param, value]) => {
      message = message.replace(`{${param}}`, value);
    });

    return message;
  }

  getDefaultErrorMessage(key) {
    const defaults = {
      form_error_required: "This field is required",
      form_error_email: "Please enter a valid email address",
      form_error_pattern: "Invalid format",
      form_error_min_length: "Minimum {min} characters required",
      form_error_max_length: "Maximum {max} characters allowed",
    };
    return defaults[key] || "Invalid input";
  }

  showError(input, message) {
    const errorDiv = document.createElement("div");
    errorDiv.className = "error-message";
    errorDiv.style.color = "#dc2626"; // Red color
    errorDiv.style.fontSize = "0.875rem"; // 14px
    errorDiv.style.marginTop = "0.25rem"; // 4px
    errorDiv.textContent = message;

    // Insert error message after the input
    input.parentNode.insertBefore(errorDiv, input.nextSibling);

    // Add error class to input
    input.classList.add("error");

    // Store error message
    this.errorMessages.set(input, errorDiv);
  }

  removeError(input) {
    const errorDiv = this.errorMessages.get(input);
    if (errorDiv) {
      errorDiv.remove();
      this.errorMessages.delete(input);
      input.classList.remove("error");
    }
  }

  getFieldName(input) {
    // Get label text if available
    const label = input.labels?.[0]?.textContent;
    if (label) {
      return label.replace("*", "").trim();
    }

    // Fallback to input name or placeholder
    return input.name || input.placeholder || "This field";
  }
}

// Function to check if a form should have validation
function shouldValidateForm(form) {
  // Get form action, method, and other attributes
  const action = form.getAttribute("action") || "";
  const method = form.getAttribute("method") || "get";
  const formClass = form.className || "";
  const formId = form.id || "";

  // Exclude e-commerce related forms
  const excludePatterns = [
    // Product forms
    /product-form/i,
    /add-to-cart/i,
    /cart/i,
    /checkout/i,

    // Search forms
    /search/i,
    /predictive-search/i,

    // Shopify specific forms
    /\/cart\//,
    /\/products\//,
    /localization/i,
    /currency/i,
    /language/i,

    // Newsletter signup forms that are handled by external services
    /brevo/i,
    /klaviyo/i,

    // Filter forms
    /filter/i,
    /collection/i,

    // Variant picker forms
    /variant/i,

    // Quick add forms
    /quick-add/i,
    /quick-buy/i,

    // SIB form
    /sib-form/i,
  ];

  // Check if form should be excluded
  for (const pattern of excludePatterns) {
    if (
      pattern.test(action) ||
      pattern.test(formClass) ||
      pattern.test(formId)
    ) {
      return false;
    }
  }

  // Include forms that are likely user input forms
  const includePatterns = [
    /contact/i,
    /support/i,
    /inquiry/i,
    /feedback/i,
    /question/i,
    /address/i,
    /profile/i,
    /account/i,
    // /newsletter/i,
    /subscription/i,
  ];

  // Check if form should be included
  for (const pattern of includePatterns) {
    if (
      pattern.test(action) ||
      pattern.test(formClass) ||
      pattern.test(formId)
    ) {
      return true;
    }
  }

  // For forms with specific input types that indicate user input
  const userInputTypes = ["email", "tel", "text", "textarea", "password"];
  const hasUserInputs = Array.from(
    form.querySelectorAll("input, textarea", "password")
  ).some((input) => {
    return (
      userInputTypes.includes(input.type) &&
      input.name &&
      !input.name.includes("product") &&
      !input.name.includes("variant") &&
      !input.name.includes("cart") &&
      !input.name.includes("search")
    );
  });

  // Only validate if it has user inputs and doesn't look like an e-commerce form
  return hasUserInputs && method.toLowerCase() === "post";
}

// Initialize form validation for specific forms only
document.addEventListener("DOMContentLoaded", () => {
  // Get theme settings from meta tag
  const themeSettingsScript = document.querySelector(
    'script[type="application/json"][data-theme-settings]'
  );
  if (themeSettingsScript) {
    try {
      window.themeSettings = JSON.parse(themeSettingsScript.textContent);
    } catch (e) {
      console.error("Error parsing theme settings:", e);
      window.themeSettings = {};
    }
  }

  // Only apply validation to appropriate forms
  document.querySelectorAll("form").forEach((form) => {
    if (shouldValidateForm(form)) {
      console.log(
        "Applying validation to form:",
        form.id || form.className || "unnamed form"
      );
      new FormValidator(form);
    }
  });
});
